import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card } from '@/components/ui/card';
import { ShipWheel, MessageSquare, Puzzle, Clock } from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import awsLightLogo from '../../../assets/icons/aws.svg';
import awsDarkLogo from '../../../assets/icons/aws-dark.svg';
import slackColorIcon from '../../../assets/icons/slack-color.png';
import pagerdutyIcon from '../../../assets/icons/pagerduty-icon.svg';
import posthogIcon from '../../../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../../../assets/icons/posthog-light.png';
import vercelIcon from '../../../assets/icons/vercel.svg';
import vercelLightIcon from '../../../assets/icons/vercel-light.svg';
import railwayIcon from '../../../assets/icons/railway.svg';
import flyioIcon from '../../../assets/icons/flyio.svg';
import nebiusIcon from '../../../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../../../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../../../assets/icons/daytona.svg';
import supabaseIcon from '../../../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../../../assets/icons/planetscale-mono.svg';
import neonIcon from '../../../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../../../assets/icons/clickhouse.svg';
import jenkinsIcon from '../../../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../../../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../../../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../../../assets/icons/okta-mono.svg';
import databricksIcon from '../../../assets/icons/databricks.png';
import { ReactComponent as GCPNodeIcon } from '../../../assets/icons/gcp.svg';
import terraformIcon from '../../../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../../../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../../../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../../../assets/icons/infisical.svg';
import datadogLightIcon from '../../../assets/icons/datadog-light-mode.svg';
import cloudflareIcon from '../../../assets/icons/cloudflare.svg';
import datadogDarkIcon from '../../../assets/icons/datadog-dark-mode.png';
import argocdIcon from '../../../assets/icons/argocd.svg';
import fluxcdIcon from '../../../assets/icons/flux-cd.svg';
import karpenterIcon from '../../../assets/icons/karpenter.svg';
import kyvernoIcon from '../../../assets/icons/kyverno.svg';
import trivyIcon from '../../../assets/icons/trivy.svg';
import helmLightIcon from '../../../assets/icons/helm-light.svg';
import helmDarkIcon from '../../../assets/icons/helm-dark.svg';
import gitlabIcon from '../../../assets/icons/gitlab-clean.svg';

function useIsDark() {
  const { theme } = useTheme();
  return theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

type TriggerStyle = 'k8s' | 'cloud' | 'gcp' | 'request' | 'pagerduty' | 'posthog' | 'vercel' | 'railway' | 'flyio' | 'nebius' | 'beam' | 'daytona' | 'supabase' | 'planetscale' | 'neon' | 'clickhouse' | 'jenkins' | 'circleci' | 'sonarcloud' | 'okta' | 'databricks' | 'terraform' | 'pulumi' | 'vault' | 'infisical' | 'github' | 'custom_webhook' | 'datadog' | 'cloudflare' | 'schedule' | 'karpenter' | 'kyverno' | 'trivy';

function getTriggerStyle(data: any): TriggerStyle {
  if (data.source === 'aws') return 'cloud';
  if (data.source === 'pagerduty') return 'pagerduty';
  if (data.source === 'posthog') return 'posthog';
  if (data.source === 'vercel') return 'vercel';
  if (data.source === 'railway') return 'railway';
  if (data.source === 'flyio') return 'flyio';
  if (data.source === 'nebius') return 'nebius';
  if (data.source === 'beam') return 'beam';
  if (data.source === 'daytona') return 'daytona';
  if (data.source === 'supabase') return 'supabase';
  if (data.source === 'planetscale') return 'planetscale';
  if (data.source === 'neon') return 'neon';
  if (data.source === 'clickhouse') return 'clickhouse';
  if (data.source === 'jenkins') return 'jenkins';
  if (data.source === 'circleci') return 'circleci';
  if (data.source === 'sonarcloud') return 'sonarcloud';
  if (data.source === 'okta') return 'okta';
  if (data.source === 'databricks') return 'databricks';
  if (data.source === 'gcp') return 'gcp';
  if (data.source === 'terraform') return 'terraform';
  if (data.source === 'pulumi') return 'pulumi';
  if (data.source === 'vault') return 'vault';
  if (data.source === 'infisical') return 'infisical';
  if (data.source === 'github') return 'github';
  if (data.source === 'custom_webhook') return 'custom_webhook';
  if (data.source === 'request') return 'request';
  if (data.source === 'datadog') return 'datadog';
  if (data.source === 'cloudflare') return 'cloudflare';
  if (data.source === 'schedule') return 'schedule';
  if (data.source === 'karpenter') return 'karpenter';
  if (data.source === 'kyverno') return 'kyverno';
  if (data.source === 'trivy') return 'trivy';
  return 'k8s';
}

export function TriggerNode({ data, selected }: any) {
  const isDark = useIsDark();
  const style = getTriggerStyle(data);

  const colors = {
    border: selected ? 'border-primary shadow-lg ring-2 ring-primary/20' : 'border-zinc-300 dark:border-zinc-700',
    bg: 'bg-zinc-50 dark:bg-zinc-900/40',
    iconBg: 'bg-zinc-100 dark:bg-zinc-800',
    label: 'text-zinc-500 dark:text-zinc-400',
    badge: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400',
    handle: '!bg-zinc-400',
  };

  // Custom webhook: override with user-defined color if available
  const customColor = data.custom_color;
  const customIcon = isDark
    ? (data.custom_icon_url_dark || data.custom_icon_url)
    : (data.custom_icon_url || data.custom_icon_url_dark);
  const customBorderStyle = style === 'custom_webhook' && customColor
    ? { borderColor: customColor }
    : undefined;
  const customIconBgStyle = style === 'custom_webhook' && customColor
    ? { backgroundColor: `${customColor}20` }
    : undefined;

  return (
    <div className="relative">
      <Handle type="source" position={Position.Right} className={`${colors.handle} !w-2 !h-2`} />
      <Card
        className={`w-[260px] p-3 border-2 transition-shadow cursor-pointer ${colors.border} ${colors.bg}`}
        style={customBorderStyle}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${colors.iconBg}`} style={customIconBgStyle}>
            {style === 'custom_webhook' && customIcon ? (
              <img src={customIcon} alt="Custom" className="w-4 h-4 object-contain" />
            ) : style === 'custom_webhook' ? (
              <Puzzle className={`w-4 h-4 ${colors.label}`} />
            ) : style === 'cloud' ? (
              <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-4 h-4" />
            ) : style === 'request' ? (
              (() => {
                const cat = (data.signal_filter?.request_categories || [])[0] || '';
                switch (cat) {
                  case 'k8s': return <ShipWheel className="w-4 h-4 text-blue-500" />;
                  case 'cloud': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-4 h-4" />;
                  case 'cloudflare': return <img src={cloudflareIcon} alt="Cloudflare" className="w-4 h-4" />;
                  case 'pagerduty': return <img src={pagerdutyIcon} alt="PagerDuty" className="w-4 h-4" />;
                  case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className="w-4 h-4" />;
                  case 'argocd': return <img src={argocdIcon} alt="ArgoCD" className="w-4 h-4" />;
                  case 'argo-rollouts': return <img src={argocdIcon} alt="Argo Rollouts" className="w-4 h-4" />;
                  case 'fluxcd': return <img src={fluxcdIcon} alt="Flux CD" className="w-4 h-4" />;
                  case 'karpenter': return <img src={karpenterIcon} alt="Karpenter" className="w-4 h-4" />;
                  case 'kyverno': return <img src={kyvernoIcon} alt="Kyverno" className="w-4 h-4" />;
                  case 'trivy': return <img src={trivyIcon} alt="Trivy" className="w-4 h-4" />;
                  case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-4 h-4" />;
                  case 'gitlab': return <img src={gitlabIcon} alt="GitLab" className="w-4 h-4" />;
                  case 'helm': return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" className="w-4 h-4" />;
                  case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className="w-4 h-4" />;
                  case 'railway': return <img src={railwayIcon} alt="Railway" className={`w-4 h-4 object-contain`} style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
                  case 'flyio': return <img src={flyioIcon} alt="Fly.io" className={`w-4 h-4 object-contain`} />;
                  case 'nebius': return <img src={nebiusIcon} alt="Nebius" className={`w-4 h-4 object-contain`} />;
                  case 'beam': return <BeamIcon className={`w-4 h-4 text-foreground`} />;
                  case 'daytona': return <DaytonaIcon className={`w-4 h-4 text-foreground`} />;
                  case 'supabase': return <img src={supabaseIcon} alt="Supabase" className={`w-4 h-4 object-contain`} />;
                  case 'planetscale': return <PlanetScaleIcon className={`w-4 h-4 text-foreground`} />;
                  case 'neon': return <img src={neonIcon} alt="Neon" className={`w-4 h-4 object-contain`} />;
                  case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" className={`w-4 h-4 object-contain`} />;
                  case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" className={`w-4 h-4 object-contain`} />;
                  case 'circleci': return <CircleCIIcon className={`w-4 h-4 text-foreground`} />;
                  case 'sonarcloud': return <SonarCloudIcon className={`w-4 h-4 text-foreground`} />;
                  case 'okta': return <OktaIcon className={`w-4 h-4 text-foreground`} />;
                  case 'databricks': return <img src={databricksIcon} alt="Databricks" className={`w-4 h-4 object-contain`} />;
                  case 'gcp': return <GCPNodeIcon className="w-4 h-4" />;
                  case 'terraform': return <img src={terraformIcon} alt="Terraform" className={`w-4 h-4 object-contain`} />;
                  case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" className={`w-4 h-4 object-contain`} />;
                  case 'vault': return <img src={vaultIcon} alt="Vault" className={`w-4 h-4 object-contain`} />;
                  case 'infisical': return <img src={infisicalIcon} alt="Infisical" className={`w-4 h-4 object-contain rounded-sm`} />;
                  default: return <img src={slackColorIcon} alt="Request" className="w-4 h-4" />;
                }
              })()
            ) : style === 'pagerduty' ? (
              <img src={pagerdutyIcon} alt="PagerDuty" className="w-4 h-4" />
            ) : style === 'posthog' ? (
              <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className="w-4 h-4 object-contain" />
            ) : style === 'vercel' ? (
              <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className="w-4 h-4" />
            ) : style === 'railway' ? (
              <img src={railwayIcon} alt="Railway" className="w-4 h-4 object-contain" style={{ filter: isDark ? 'invert(1)' : 'none' }} />
            ) : style === 'flyio' ? (
              <img src={flyioIcon} alt="Fly.io" className="w-4 h-4 object-contain" />
            ) : style === 'nebius' ? (
              <img src={nebiusIcon} alt="Nebius" className="w-4 h-4 object-contain" />
            ) : style === 'beam' ? (
              <BeamIcon className="w-4 h-4 text-foreground" />
            ) : style === 'daytona' ? (
              <DaytonaIcon className="w-4 h-4 text-foreground" />
            ) : style === 'supabase' ? (
              <img src={supabaseIcon} alt="Supabase" className="w-4 h-4 object-contain" />
            ) : style === 'planetscale' ? (
              <PlanetScaleIcon className="w-4 h-4 text-foreground" />
            ) : style === 'neon' ? (
              <img src={neonIcon} alt="Neon" className="w-4 h-4 object-contain" />
            ) : style === 'clickhouse' ? (
              <img src={clickhouseIcon} alt="ClickHouse" className="w-4 h-4 object-contain" />
            ) : style === 'jenkins' ? (
              <img src={jenkinsIcon} alt="Jenkins" className="w-4 h-4 object-contain" />
            ) : style === 'circleci' ? (
              <CircleCIIcon className="w-4 h-4 text-foreground" />
            ) : style === 'sonarcloud' ? (
              <SonarCloudIcon className="w-4 h-4 text-foreground" />
            ) : style === 'okta' ? (
              <OktaIcon className="w-4 h-4 text-foreground" />
            ) : style === 'databricks' ? (
              <img src={databricksIcon} alt="Databricks" className="w-4 h-4 object-contain" />
            ) : style === 'gcp' ? (
              <GCPNodeIcon className="w-4 h-4" />
            ) : style === 'terraform' ? (
              <img src={terraformIcon} alt="Terraform" className="w-4 h-4 object-contain" />
            ) : style === 'pulumi' ? (
              <img src={pulumiIcon} alt="Pulumi" className="w-4 h-4 object-contain" />
            ) : style === 'vault' ? (
              <img src={vaultIcon} alt="Vault" className="w-4 h-4 object-contain" />
            ) : style === 'infisical' ? (
              <img src={infisicalIcon} alt="Infisical" className="w-4 h-4 object-contain rounded-sm" />
            ) : style === 'github' ? (
              <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-4 h-4" />
            ) : style === 'datadog' ? (
              <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className="w-4 h-4" />
            ) : style === 'cloudflare' ? (
              <img src={cloudflareIcon} alt="Cloudflare" className="w-4 h-4" />
            ) : style === 'schedule' ? (
              <Clock className="w-4 h-4 text-zinc-900 dark:text-zinc-100" />
            ) : style === 'karpenter' ? (
              <img src={karpenterIcon} alt="Karpenter" className="w-4 h-4 object-contain" />
            ) : style === 'kyverno' ? (
              <img src={kyvernoIcon} alt="Kyverno" className="w-4 h-4 object-contain" />
            ) : style === 'trivy' ? (
              <img src={trivyIcon} alt="Trivy" className="w-4 h-4 object-contain" />
            ) : (
              <ShipWheel className="w-4 h-4 text-blue-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-medium uppercase tracking-wide ${colors.label}`}
              style={style === 'custom_webhook' && customColor ? { color: customColor } : undefined}
            >{style === 'custom_webhook' && data.integration_name ? data.integration_name : 'Trigger'}</div>
            <div className="text-sm font-semibold truncate">{data.label || 'Signal'}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
